import { App } from 'App';
import { datadogLogs } from '@datadog/browser-logs';
import { SESSION_MOBILE_INSTANCE_ID } from 'utils/constants';
import Cookie from 'js-cookie';

import ReactGA from 'react-ga';

type Cookie_Type = {
  name: string;
  value: string;
};

// Regex provided by BE to check if GA cookies are valid
const BE_GA_REGEX = /^GA\d\.\d+\.\d+\.\d+$/;
const BE_GA_MEASUREMENT_REGEX =
  /^GS\d\.\d+\.s\d+\$o\d+\$g\d+\$t\d+\$j\d+\$l\d+\$h\d+(\$d[a-zA-Z\d]{34})?$/;

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

export const useAnalyticsCookies = () => {
  const applicationInstanceId = sessionStorage.getItem(
    SESSION_MOBILE_INSTANCE_ID,
  );

  const queryParams = applicationInstanceId
    ? `?applicationInstanceId=${applicationInstanceId}`
    : undefined;

  const { fetch: postAnalyticsCookies } = App.useApi(
    'client/cookies',
    'POST',
    queryParams,
  );

  const totalCookies = MEASUREMENT_ID ? 2 : 1;

  const getInitialGaCookie = () => {
    const gaValue = Cookie.get('_ga');
    if (gaValue && BE_GA_REGEX.test(gaValue)) {
      return {
        name: 'ga',
        value: gaValue,
      };
    }
  };

  // Get second cookie name
  const getMeasurementCookieName = () => {
    if (!MEASUREMENT_ID) return undefined;

    const [, secondPart] = (MEASUREMENT_ID || '').split('-');

    return `ga_${secondPart}`;
  };

  //  get second cookie
  const getMeasurementCookie = () => {
    const cookieName = getMeasurementCookieName();
    const qaValue = Cookie.get(`_${cookieName}`);

    if (qaValue && cookieName && BE_GA_MEASUREMENT_REGEX.test(qaValue)) {
      return {
        name: cookieName,
        value: qaValue,
      };
    }

    return undefined;
  };

  // check consents and remove cookies in case user rejected them
  const areConsentsForCookiesAccepted = () => {
    const consentsCookiesValue = Cookie.get('CookieInformationConsent');
    if (!consentsCookiesValue) return true; // create cookies if client didn't rejected them
    const decoded = decodeURIComponent(consentsCookiesValue);
    const consentData = decoded?.length && JSON.parse(decoded);
    return (
      consentData &&
      consentData.consents_approved.includes('cookie_cat_statistic')
    );
  };

  const getAnalyticsCookies = () => {
    // check consents
    const areCookiesAccepted =
      Boolean(import.meta.env.VITE_GA) && areConsentsForCookiesAccepted();
    if (!areCookiesAccepted) return [];

    // initialize cookies if they don't exist
    initializeCookiesIfNeeded();

    const analyticsCookies: Cookie_Type[] = [];

    const gaCookie = getInitialGaCookie();

    if (gaCookie) {
      analyticsCookies.push(gaCookie);
    }

    if (analyticsCookies.length === totalCookies) {
      return analyticsCookies;
    }

    const measurement = getMeasurementCookie();
    if (measurement) {
      analyticsCookies.push(measurement);
    }

    return analyticsCookies;
  };

  // initialize cookies in case they don't exist
  const initializeCookiesIfNeeded = () => {
    if (!import.meta.env.VITE_GA) return;

    const gaCookieExists = Boolean(Cookie.get('_ga'));
    const ga2CookieName = MEASUREMENT_ID && getMeasurementCookieName();

    const ga2CookieExists =
      !MEASUREMENT_ID || Boolean(ga2CookieName && Cookie.get(ga2CookieName));

    if (!gaCookieExists || !ga2CookieExists) {
      ReactGA.initialize(import.meta.env.VITE_GA);
      ReactGA.send({ hitType: 'pageview' });
    }
  };

  const sendAnalyticsCookiesIfExists = async ({
    applicationId,
    clientId,
  }: {
    applicationId?: number | undefined;
    clientId?: number | undefined;
  } = {}) => {
    const registerLog = Boolean(applicationId) && Boolean(clientId);
    const gaCookies = getAnalyticsCookies();

    if (gaCookies.length > 0) {
      // log to investigate how many clients don't have valid cookies on PROD
      if (gaCookies.length < 2 && import.meta.env.VITE_ENV === 'production') {
        datadogLogs.logger.error(`gacookies missing. gaCookies - ${gaCookies}`);
      }

      // cookies length + success + failure log -> clientid +appicationid
      try {
        await postAnalyticsCookies({
          body: gaCookies as any, //TODO: Ditch the custom query system and replace with react-query
          headers: {
            'Content-Type': 'application/vnd.analytics.v2+json',
          },
        });
        if (registerLog) {
          datadogLogs.logger.info(
            `gacookies success - app: ${applicationId} + client: ${clientId}`,
          );
        }
      } catch {
        if (registerLog) {
          datadogLogs.logger.error(
            `gacookies error - app: ${applicationId} + client: ${clientId}`,
          );
        }
      }
    }
  };

  return {
    initializeCookiesIfNeeded,
    getAnalyticsCookies,
    sendAnalyticsCookiesIfExists,
  };
};
